import { useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, LogOut, Plus } from "lucide-react";
import { AiSettingsCard } from "@/components/settings/ai-settings-card";
import { ApiKeyList } from "@/components/settings/api-key-list";
import { CloudCard } from "@/components/settings/cloud-card";
import { CreateApiKeyDialog } from "@/components/settings/create-api-key-dialog";
import { GitHubCard } from "@/components/settings/github-card";
import { SlackCard } from "@/components/settings/slack-card";
import { MachineList } from "@/components/machines/machine-list";
import { adminList } from "@/components/onboarding/onboarding-guard";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import { useRevokeToken, useRotateToken, useTokens } from "@/hooks/queries/use-tokens";
import { useUpdateUser } from "@/hooks/queries/use-users";
import { useBrandTheme, type BrandTheme } from "@/hooks/use-brand-theme";
import { useNewLook } from "@/hooks/use-new-look";
import { usePageTitle } from "@/hooks/use-page-title";
import { getInitials } from "@/lib/get-initials";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";
import { AutomationDefaults } from "./automation-defaults";
import { IdeaModeSetting } from "./idea-mode-setting";

/**
 * The new look's Settings (docs/new-look/PLAN.md, step 8.1): today's settings
 * in the same sections, restyled, with a section list to jump around.
 * The cards for AI keys, GitHub, cloud, Slack, machines and API keys are today's.
 */

export const SECTIONS = [
  { id: "account", label: "Account" },
  { id: "preferences", label: "Preferences" },
  { id: "machines", label: "Machines" },
  { id: "ai", label: "AI keys" },
  { id: "github", label: "GitHub and cloud" },
  { id: "slack", label: "Slack" },
  { id: "automation", label: "Automation defaults" },
  { id: "api-keys", label: "API keys" },
  { id: "billing", label: "Billing and members" },
] as const;

const TIMEZONES: string[] =
  typeof (Intl as Record<string, unknown>).supportedValuesOf === "function"
    ? (Intl as unknown as { supportedValuesOf(key: string): string[] }).supportedValuesOf(
        "timeZone"
      )
    : ["UTC", "America/New_York", "America/Los_Angeles", "Europe/London", "Asia/Kolkata"];

const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const MAX_AVATAR_BYTES = 512 * 1024;

/** The app's two looks: light with blue, dark with orange (the brand theme). */
const BRANDS: { value: BrandTheme; label: string }[] = [
  { value: "blue", label: "Light (blue)" },
  { value: "orange", label: "Dark (orange)" },
];

const FIELD =
  "min-h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground disabled:opacity-60";

function Section({
  id,
  title,
  hint,
  action,
  children,
}: {
  id: string;
  title: string;
  hint?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-title`}
      className="scroll-mt-24 rounded-2xl border border-border bg-background p-4 sm:p-6"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={`${id}-title`} className="text-lg font-semibold text-foreground">
            {title}
          </h2>
          {hint && <p className="mt-0.5 text-sm text-muted-foreground">{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 border-b border-border py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        {hint && <div className="text-[13px] text-muted-foreground">{hint}</div>}
      </div>
      <div className="sm:w-56">{children}</div>
    </div>
  );
}

function Account() {
  const user = useAuthStore((s) => s.user);
  const updateUser = useUpdateUser();
  const { data: onboarding } = useOnboardingState();
  const org = onboarding?.step === "ready" && onboarding.organizationName ? onboarding : null;
  const [name, setName] = useState(user?.name ?? "");
  const [timeZone, setTimeZone] = useState(user?.timeZone ?? BROWSER_TIMEZONE);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ?? "");
  const [photoError, setPhotoError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const pickPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (picked.size > MAX_AVATAR_BYTES) {
      setPhotoError("Pick an image under 512 KB.");
      return;
    }
    setPhotoError(null);
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarUrl(reader.result as string);
    };
    reader.readAsDataURL(picked);
  };

  const save = () => {
    if (!user) return;
    updateUser.mutate(
      { id: user.id, data: { name, timeZone, ...(avatarUrl ? { avatarUrl } : {}) } },
      {
        onSuccess: (res) => {
          // Keep the sign-in fields PUT /users/:id doesn't return (as today's page does).
          const current = useAuthStore.getState().user;
          useAuthStore.setState({
            user: {
              ...(current ?? {}),
              ...res.data,
              ...(current
                ? {
                    isGlobalAdmin: current.isGlobalAdmin,
                    role: current.role,
                    teamId: current.teamId,
                  }
                : {}),
            },
          });
        },
      }
    );
  };

  return (
    <Section id="account" title="Account" hint="Your name, photo and time zone.">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => file.current?.click()}
            aria-label="Change your photo"
            className="group relative h-20 w-20 overflow-hidden rounded-full bg-primary/10 text-2xl font-semibold text-primary"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center">
                {getInitials([name, user?.name, "?"].find(Boolean) ?? "?")}
              </span>
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
              <Camera className="h-5 w-5 text-white" aria-hidden="true" />
            </span>
          </button>
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={pickPhoto}
          />
          <span className="text-xs text-muted-foreground">Up to 512 KB</span>
          {photoError && (
            <span role="alert" className="text-xs text-red-700 dark:text-red-300">
              {photoError}
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted-foreground">Name</span>
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              className={FIELD}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted-foreground">Email</span>
            <input value={user?.email ?? ""} disabled className={FIELD} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted-foreground">Time zone</span>
            <select
              value={timeZone}
              onChange={(e) => {
                setTimeZone(e.target.value);
              }}
              className={FIELD}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          {org && (
            <div className="text-sm" data-testid="settings-organization">
              <span className="text-muted-foreground">Organization: </span>
              <span className="font-medium text-foreground">{org.organizationName}</span>
              <span className="text-muted-foreground">
                {" "}
                · {org.organizationRole === "admin" ? "Admin" : "Member"}
              </span>
              {org.organizationRole !== "admin" && org.admins.length > 0 && (
                <p className="mt-1 text-[13px] text-muted-foreground">
                  Admins: {adminList(org.admins)}
                </p>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={updateUser.isPending}
              className="min-h-11 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {updateUser.isPending ? "Saving…" : "Save"}
            </button>
            {updateUser.isSuccess && <span className="text-sm text-muted-foreground">Saved.</span>}
            {updateUser.isError && (
              <span role="alert" className="text-sm text-red-700 dark:text-red-300">
                Couldn't save. Try again.
              </span>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}

function Preferences() {
  const { brand, setBrand } = useBrandTheme();
  const [newLook, setNewLook] = useNewLook();
  return (
    <Section
      id="preferences"
      title="Preferences"
      hint="How CompBox looks for you, on this browser."
    >
      <Row label="Look" hint="Light or dark. The switch in the header does the same.">
        <select
          aria-label="Look"
          value={brand}
          onChange={(e) => {
            setBrand(e.target.value as BrandTheme);
          }}
          className={FIELD}
        >
          {BRANDS.map((b) => (
            <option key={b.value} value={b.value}>
              {b.label}
            </option>
          ))}
        </select>
      </Row>
      <Row label="Try the new look" hint="Switch back to the classic look any time.">
        <button
          type="button"
          role="switch"
          aria-checked={newLook}
          aria-label="Try the new look"
          onClick={() => {
            setNewLook(!newLook);
          }}
          className={cn(
            "relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            newLook ? "bg-primary" : "bg-muted-foreground/30"
          )}
        >
          <span
            className={cn(
              "inline-block h-6 w-6 rounded-full bg-white shadow transition-transform",
              newLook ? "translate-x-5" : "translate-x-0.5"
            )}
          />
        </button>
      </Row>
    </Section>
  );
}

function ApiKeys() {
  const [creating, setCreating] = useState(false);
  const { data: tokens, isLoading } = useTokens();
  const revoke = useRevokeToken();
  const rotate = useRotateToken();
  return (
    <Section
      id="api-keys"
      title="API keys"
      hint="Keys that let tools like the CompBox app and MCP server act as you."
      action={
        <button
          type="button"
          onClick={() => {
            setCreating(true);
          }}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-border px-4 text-sm font-semibold text-foreground hover:bg-muted"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          New key
        </button>
      }
    >
      {isLoading ? (
        <div className="h-16 animate-pulse rounded-lg bg-muted" />
      ) : (
        <ApiKeyList
          tokens={tokens ?? []}
          onRevoke={(id) => {
            revoke.mutate(id);
          }}
          isRevoking={revoke.isPending}
          onRotate={(id) => rotate.mutateAsync(id)}
          isRotating={rotate.isPending}
        />
      )}
      <CreateApiKeyDialog open={creating} onOpenChange={setCreating} />
    </Section>
  );
}

function LinkRow({ to, label, hint }: { to: string; label: string; hint: string }) {
  return (
    <Link
      to={to}
      className="flex min-h-14 items-center justify-between gap-3 border-b border-border py-2 last:border-b-0 hover:text-primary"
    >
      <span>
        <span className="block text-sm font-medium text-foreground">{label}</span>
        <span className="block text-[13px] text-muted-foreground">{hint}</span>
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </Link>
  );
}

function jump(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function NewSettingsPage() {
  usePageTitle("Settings");
  const { user, logout } = useAuthStore();

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Settings</h1>
          <p className="mt-1 truncate text-sm text-muted-foreground">{user?.email}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            logout();
          }}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          Sign out
        </button>
      </div>

      <div className="grid gap-5 lg:grid-cols-[200px_minmax(0,1fr)] lg:items-start">
        <nav
          aria-label="Settings sections"
          className="flex flex-wrap gap-1.5 lg:sticky lg:top-24 lg:flex-col lg:gap-0.5"
        >
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                jump(s.id);
              }}
              className="min-h-10 rounded-lg border border-border px-3 text-left text-sm text-muted-foreground hover:bg-muted hover:text-foreground lg:border-0"
            >
              {s.label}
            </button>
          ))}
        </nav>

        <div className="flex min-w-0 flex-col gap-5">
          <Account />
          <Preferences />
          <Section
            id="machines"
            title="Machines"
            hint="Computers paired with your account that can run builds. Rename one, or remove it to revoke its access."
            action={
              <Link
                to="/connect"
                className="inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-border px-4 text-sm font-semibold text-foreground hover:bg-muted"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Connect a computer
              </Link>
            }
          >
            <MachineList />
          </Section>
          <Section id="ai" title="AI keys" hint="Which AI account your runs use.">
            <AiSettingsCard />
          </Section>
          <Section
            id="github"
            title="GitHub and cloud"
            hint="Where your code lives and where it deploys."
          >
            <div className="flex flex-col gap-6">
              <GitHubCard />
              <div id="cloud" className="scroll-mt-24 border-t border-border pt-6">
                <CloudCard />
              </div>
            </div>
          </Section>
          <Section
            id="slack"
            title="Slack"
            hint="Build updates in a channel, and messages to you when a run needs you."
          >
            <SlackCard />
          </Section>
          <Section
            id="automation"
            title="Automation defaults"
            hint="What runs do on their own: your ideas' default mode, and each project's settings."
          >
            <IdeaModeSetting />
            <AutomationDefaults />
          </Section>
          <ApiKeys />
          <Section
            id="billing"
            title="Billing and members"
            hint="Your plan, and who's in your organization."
          >
            <LinkRow to="/billing" label="Billing" hint="Plan, seats and invoices" />
            <LinkRow
              to="/organization/members"
              label="Members"
              hint="Invite people and set who's an admin"
            />
          </Section>
        </div>
      </div>
    </div>
  );
}
